import partition from 'lodash/partition';
import { ASSOCIATION_SOURCE_TYPES } from '../../constants';
import {
  CO_OBJECT_TYPE_PIPELINE,
  CO_RELATION_TYPE_BOTH,
  CO_RELATION_TYPE_ONE_TO_ONE,
  CO_RELATION_TYPE_PRIMARY,
  CO_OBJECT_TYPE_STANDARD,
} from '../../utilities';
import { v4 as uuidv4 } from 'uuid';
import { isContact } from 'utility/fieldHelpers';
import { orderBy } from 'lodash';

export const getRelatedObjectTypeOptions = (t) => [
  {
    value: 'primary',
    label: t('Many-to-One'),
    name: (name) =>
      name
        ? t('Primary {{name}} Record', { name })
        : `${t('Primary')} {{${t('Entity B Name')}}} ${t('Record')}`,
    reverseName: (name) =>
      name
        ? t('Related {{name}} Records', {
            name,
          })
        : `${t('Related')} {{${t('Entity A Name')}}} ${t('Records')}`,
  },
  {
    value: 'one_to_one',
    label: t('One-to-One'),
    name: (name) => {
      return name
        ? t('Primary {{name}} Record', { name })
        : `${t('Primary')} {{${t('Entity B Name')}}} ${t('Record')}`;
    },
    reverseName: (name) =>
      name
        ? t('Primary {{name}} Record', { name })
        : `${t('Primary')} {{${t('Entity A Name')}}} ${t('Record')}`,
  },
  {
    value: 'additional',
    label: t('Many-to-Many'),
    name: (name) =>
      name
        ? t('Related {{name}} Records', { name: name })
        : `${t('Related')} {{${t('Entity B Name')}}} ${t('Records')}`,
    reverseName: (name) =>
      name
        ? t('Related {{name}} Records', {
            name,
          })
        : `${t('Related')} {{${t('Entity A Name')}}} ${t('Records')}`,
  },
  {
    value: 'primary_for',
    label: t('One-to-Many'),
    name: (name) => {
      return name
        ? t('Related {{name}} Records', { name })
        : `${t('Related')} {{${t('Entity B Name')}}} ${t('Records')}`;
    },
    reverseName: (name) =>
      name
        ? t('Primary {{name}} Record', { name })
        : `${t('Primary')} {{${t('Entity A Name')}}} ${t('Record')}`,
  },
  {
    value: 'additional_for',
    label: t('Many-to-Many'),
    name: (name) =>
      name
        ? t('Related {{name}} Records', { name: name })
        : `${t('Related')} {{${t('Entity B Name')}}} ${t('Records')}`,
    reverseName: (name) =>
      name
        ? t('Related {{name}} Records', {
            name,
          })
        : `${t('Related')} {{${t('Entity A Name')}}} ${t('Records')}`,
  },
];

export const DEFAULT_RELATION_PROPS = {
  isDefault: false,
  fieldType: 'relationship',
  isRequired: false,
  isReadOnly: false,
  isHidden: false,
};

export const getDefaultFields = (relationType, t) => {
  const dictionary = getRelatedObjectTypeOptions(t).find(
    ({ value: valueId }) => valueId === relationType
  );
  const fieldId = uuidv4();
  return {
    relatedObject: null,
    relationType: relationType,
    isNew: true,
    fieldId,
    field: {
      id: fieldId,
      displayName: dictionary.name(),
      relation: {
        relatedField: uuidv4(),
        relatedName: dictionary.reverseName(),
        rollupTimeline: false,
        inverseRelationRollupTimeline: false,
        rollupLeadsources: false,
        inverseRelationRollupLeadsources: false,
      },
    },
  };
};

export const createRelatedObjectsStepState = (model) => {
  // Separate-out related objects from reverse relations
  const [relatedObjects, relatedObjectsReverse] = partition(
    model.relatedObjects,
    ({ relationType }) =>
      [
        CO_RELATION_TYPE_PRIMARY,
        CO_RELATION_TYPE_ONE_TO_ONE,
        CO_RELATION_TYPE_BOTH,
      ].includes(relationType)
  );
  relatedObjects.sort((a, b) => {
    // if (a.isNew && !b.isNew) return 1;
    // if (!a.isNew && b.isNew) return -1;
    return (a?.field?.order ?? 0) - (b?.field?.order ?? 0);
  });

  relatedObjectsReverse.sort((a, b) => {
    // if (a.isNew && !b.isNew) return 1;
    // if (!a.isNew && b.isNew) return -1;
    return (a?.field?.order ?? 0) - (b?.field?.order ?? 0);
  });

  const associationSourceObjects = (model.associationSourceFields || []).reduce(
    (acc, { relatedObject }) => {
      if (!acc?.find((o) => relatedObject.id === o.value)) {
        acc.push({
          value: relatedObject.id,
          label: relatedObject.objectName,
        });
      }
      return acc;
    },
    []
  );

  const associationSourceFields = (model.associationSourceFields || []).map(
    ({ id }) => id
  );

  return {
    associationSource:
      model.associationSource || ASSOCIATION_SOURCE_TYPES.direct,
    associationSourceFields,
    associationSourceObjects,
    relatedObjects,
    relatedObjectsReverse,
  };
};

export const getRelationKey = (
  { relatedObject, relationType, field },
  index
) => {
  // There's no great key for these so we base it on their contents and index.
  // A. the contents aren't unique (e.g. for non-fully-filled-out relation), and
  // B. the index is a poor key, as it associates error message state with the
  //    index rather than the relation.
  return JSON.stringify([relatedObject, relationType, field?.id, index]);
};

export const syncSelfRelatedField = (current, updated) => ({
  ...current,
  field: {
    ...current.field,
    displayName: updated.field.relation.relatedName,
    isSuppressed: updated.field.relation.inverseRelationSuppressed,
    relation: {
      ...current.field.relation,
      inverseRelationSuppressed: updated.field.isSuppressed,
      relatedName: updated.field.displayName,
      rollupTimeline: updated.field.relation.inverseRelationRollupTimeline,
      inverseRelationRollupTimeline: updated.field.relation.rollupTimeline,
      rollupLeadsources:
        updated.field.relation.inverseRelationRollupLeadsources,
      inverseRelationRollupLeadsources:
        updated.field.relation.rollupLeadsources,
    },
  },
});

export const getAllModelOptions = (models) =>
  orderBy(
    models,
    [
      // contacts, standard, then pipeline
      isContact,
      (m) => m.objectType === CO_OBJECT_TYPE_STANDARD,
      (m) => m.objectType === CO_OBJECT_TYPE_PIPELINE,
    ],
    ['desc', 'desc', 'desc']
  ).map((model) => ({
    value: model.id,
    label: model.objectName,
    entityName: model.entityName,
    isContact: isContact(model),
  }));
